import { ref } from "vue"
import { useAxios } from "./useAxios"
import { AxiosError, AxiosResponse } from "axios"
import { Profile } from "@/interfaces/profiles/Profile"
import Cookies from "js-cookie";
import { Organization } from "@/interfaces/Organization";

const isAuthLoading = ref(false)
const isChangingPassword = ref(false)
const axios = useAxios()

export interface RegisterPayload {
    email: string
    password: string
    name: string
    phoneNumber: string
    organizationName: string
    document: string
}

export interface LoginPayload {
    email: string
    password: string
}

interface UserAuthenticated {
    userId: number;
    sessionId: string;
    email: string;
    roles: string[];
    is2AF: boolean;
    token: string;
    refreshToken: string;
    organizationIds: string[];
    tenantId: string;
    profileUuid?: string;
  }

const userAuthenticated = ref<UserAuthenticated>(JSON.parse(localStorage.getItem('authUser') ?? Cookies.get('authUser') ?? '{}'))

if (userAuthenticated.value?.userId && !userAuthenticated.value?.profileUuid) {
    axios.get(`/profile/${userAuthenticated.value.userId}`).then((res) => {
        if (res.data?.uuid) {
            userAuthenticated.value = { ...userAuthenticated.value, profileUuid: res.data.uuid }
            localStorage.setItem('authUser', JSON.stringify(userAuthenticated.value))
        }
    }).catch(() => {})
}

export function useAuth() {

    async function register(payload: RegisterPayload, onSuccess: (res: AxiosResponse) => void, onError: (error: any) => void) {
        isAuthLoading.value = true

        try {
            const res = await axios.post('/accounts', { origin: 'member-area', ...payload }, { baseURL: process.env.VUE_APP_API_REST_BASE_URL })

            onSuccess(res)
        } catch (error) {
            const axiosError = error as AxiosError<any>;
            onError(axiosError.response?.data.errors);
        } finally {
            isAuthLoading.value = false
        }
    }

    function login(payload: LoginPayload, onSuccess: (res: AxiosResponse) => void, onError: (error: any) => void) {
        isAuthLoading.value = true

        axios.post('/sessions', payload, { baseURL: process.env.VUE_APP_API_REST_BASE_URL })
            .then(async (res) => {
                localStorage.setItem('authToken', res.data.token)
                localStorage.setItem('authUserId', res.data.userId)
                localStorage.setItem('organizationId', JSON.stringify(res.data.organizationIds[0]))

                userAuthenticated.value = res.data

                try {
                    const profileRes = await axios.get(`/profile/${res.data.userId}`)
                    userAuthenticated.value = { ...userAuthenticated.value, profileUuid: profileRes.data.uuid }
                } catch {}

                localStorage.setItem('authUser', JSON.stringify(userAuthenticated.value))
                onSuccess(res)
            })
            .catch((error) => {
                onError(error)
            })
            .finally(() => isAuthLoading.value = false)
    }

    function logout(callback: Function) {
        localStorage.clear()
        callback()
    }

    function sendResetPasswordLink(email: string, onSuccess: (res: AxiosResponse) => void, onError: (error: any) => void) {
        isAuthLoading.value = true

        axios.put('/accounts/password/recovery', { email })
            .then((res) => {
                onSuccess(res)
            })
            .catch((error) => {
                onError(error)
            })
            .finally(() => isAuthLoading.value = false)
    }

    function resetPassword(payload: { token: string, password: string }, onSuccess: (res: AxiosResponse) => void, onError: (error: any) => void) {
        isAuthLoading.value = true

        axios.patch('/accounts/password/recovery', payload)
            .then((res) => {
                onSuccess(res)
            })
            .catch((error) => {
                onError(error)
            })
            .finally(() => isAuthLoading.value = false)
    }

    async function getProfile(): Promise<Profile> {
        const id = userAuthenticated.value.profileUuid ?? userAuthenticated.value.userId
        const res = await axios.get(`/profile/${id}`)

        return res.data
    }

    async function changePassword(payload: { passwordOld: string, password: string, passwordConfirm: string }, onSuccess: Function, onError: Function): Promise<boolean> {
        isChangingPassword.value = true

        try {
            await axios.put('/accounts', payload)
            onSuccess()
        } catch (e) {
            onError()
        }

        isChangingPassword.value = false

        return true
    }

    function hasRoles(roles: string[]) {
        return roles.some((roleName: string) => {
            return userAuthenticated.value?.roles?.some((role: any) => {
                return role === roleName
            })
        })
    }

    async function validate2AF(code: string, onSuccess: (res: AxiosResponse) => void, onError: (error: any) => void) {
        isAuthLoading.value = true

        try {
            const res = await axios.post('/accounts/2af/validate', { code }, { baseURL: process.env.VUE_APP_API_REST_BASE_URL })

            localStorage.setItem('authToken', res.data.token)
            localStorage.setItem('authUser', JSON.stringify(res.data))
            userAuthenticated.value = res.data

            onSuccess(res)
        } catch (error) {
            onError(error)
        } finally {
            isAuthLoading.value = false
        }
    }

    const organizationCookie = Cookies.get("organization")
    const organization: Partial<Organization> = organizationCookie ? JSON.parse(organizationCookie) : { id: userAuthenticated.value?.organizationIds ? userAuthenticated.value.organizationIds[0] : null }

    return {
        userAuthenticated: userAuthenticated.value,
        register,
        login,
        logout,
        isAuthLoading,
        sendResetPasswordLink,
        resetPassword,
        getProfile,
        changePassword,
        isChangingPassword,
        hasRoles,
        validate2AF,
        organization
    }
}
